/*
	SelectAssets can be configured to select a single asset, or (unbounded) multiple, by passing a
	`multiple` flag prop.

	If `multiple` is `false` (default), it will single select and the leftmost column will have circular radio controls.
	If `true`, the leftmost column will have square checkboxes, and a Select All checkbox in the header.

	In both cases, the `onSelect` callback will receive an array of asset ids.
*/

import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import {
	Modal,
	Layout,
	Button,
	Pager,
	Icon,
	Toolbar,
	ProgressIndicator
} from "rhombus-react";
import { noop, capFirst } from "utils/common";

import { Assets } from "reducers/Assets";
import AssetGrid from "routes/assets/Grid";
import { useCollectionFiltering } from "utils/DataGrid";

import AssetNameCell from "widgets/grid/cells/AssetName";
import TotalFileSizeColumn from "widgets/grid/cells/TotalFileSizeColumn";
import { DateColumn } from "widgets/grid/cells/DateColumn";
import RadioSelectItem from "widgets/grid/cells/RadioSelectItem";
import EmptyDash from "widgets/grid/cells/EmptyDash";
import EmptySection from "widgets/EmptySection";
import { isAfterEffectsAsset, isTextAsset } from "widgets/grid/cells/AssetActions";
import SelectAllCell from "./SelectAllCell";
import SelectItem from "./SelectItem";

import { CREATIVE_ROW_COUNT_OPTIONS, SEARCH_DEBOUNCE } from "utils/constants";

import useAssetUploader from "hooks/useAssetUploader";
import { AssetSocketListener } from "hooks/useAssetSocket";

import { buildAssetId } from "utils/Assets";

import PreviewAssetsModal from "./PreviewAssetsModal";

import "./SelectAssets.css";

export const SelectionContext = React.createContext({
	/*
		I'm not sure why, but just passing the component props to the headerRenderer component
		wasn't working to have it update on prop change.

		Using a Context did, and having a whole reducer just for this seemed like overkill.
	*/
	isAllSelected: false,
});

const SingleSelectColumn = {
	key: "idSelector",
	name: "",
	width: 60,
	formatter: RadioSelectItem,
};

const MultiSelectColumn = {
	key: "idSelector",
	name: "",
	width: 40,
	formatter: SelectItem,
};

const COLUMNS = [
	{
		...AssetNameCell,
		name: "File Name",
	},
	{
		key: "actions",
		name: "Actions",
		width: 80,
		resizable: false,
		sortable: false,
		getRowMetaData: data => data,
	},
	{
		key: "dimensions",
		name: "Dimensions",
		formatter:EmptyDash,
		width:120,
		resizable:false,
		sortable: true,
	},
	TotalFileSizeColumn,
	{
		key: "lastModified",
		name: "Last Modified",
		formatter: DateColumn,
		width: 135,
	}
];

export function isFileFormatAccepted(asset, accept){
	let acceptTypes = null
		, isAccepted = true;

	if (accept && !/\*/gi.test(accept)) { // Check if accept does not have a * (e.g. image/*, video/*, etc)
		acceptTypes = new Set(accept.replace(/ /g, '').split(',')); // Remove all whitespace and create a set of acceptable formats
	}

	if(acceptTypes) {
		const periodIndex = asset.name.lastIndexOf('.'); // Take last index in case there are periods before the extension
		if (periodIndex > 0) { // Assuming that the shortest asset name would be `x.extension`
			const extension = asset.name.slice(periodIndex).toLowerCase();
			isAccepted = acceptTypes.has(extension);
		} else { // If there is no extension in the name, disable the asset
			isAccepted = false;
		}
	}

	return isAccepted;
}

export default function SelectAssets({
	commonModalProps,
	navigationActions: { pushModal, popModal },

	Breadcrumb,

	multiple = false, // true if you want to be able to select more than one asset

	type,
	allowEncodedVideo = true,
	allowUnencodedVideo = true,
	onSelect = noop,
	configureFilter = null,

	submitButtonText,
	submitButtonTheme = "blue",

	accept,
}){
	const dispatch = useDispatch();
	const assets = useSelector(state => state.assets);

	const [selectedIds, setSelectedIds] = useState([]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const typeLabel = /^aftereffects$/i.test(type) ? "After Effects Package" : type;
	let hasSelection = (selectedIds.length > 0);

	const acceptedFileFormatAssets = useMemo(() => {
		return assets.items.filter(asset => isFileFormatAccepted(asset, accept));
	}, [assets.items, accept]);

	const canUploadVideo = (assets.columnFilters.active.encoded?.params?.[0] !== "true"); /*
		We use this column filter setting as the single source of truth for whether or not
		it's permissable for users to upload new videos.

		This can be set as a modal option here with the `allowUnencodedVideo` prop, but it can also
		be configured via the `configureFilter` prop, which overrules the former.

		If the filter is configured specifically to only allow the selection of encoded videos,
		we don't show the upload option.  We do show it if the filter is unconfigured, or configured
		to specifically show unencoded videos.
	*/
	const canUpload = /^video$/i.test(type) ? canUploadVideo : true;

	const selectFiles = useAssetUploader({
		assetType: type,
		accept,
	});

	useEffect(() => {
		// It's conventional in FT apps to clear selections when changing pages, filters, etc.
		setSelectedIds([]);
	}, [assets.items]);

	function updateMultiSelection(ids, checked){
		setSelectedIds((prevSelection) => {
			let selectedSet = new Set(prevSelection);
			if(checked){
				ids.forEach((id) => {
					selectedSet.add(id);
				});
			} else {
				ids.forEach((id) => {
					selectedSet.delete(id);
				});
			}

			return [...selectedSet];
		});
	}

	function selectAll(checked){
		if(checked){
			let enabledAssetIds = [] ;

			mappedAssets.forEach((asset) => {
				if(!asset.isDisabled) {
					enabledAssetIds.push(asset.id);
				}
			});
			setSelectedIds(enabledAssetIds);
		} else {
			setSelectedIds([]);
		}
	}

	let selector = (!multiple) ? setSelectedIds : updateMultiSelection;

	let mappedAssets = useMemo(() => {
		return assets?.items?.map((asset, idx) => {
			let isSelected = selectedIds.includes(asset.id);
			//if the asset isn't inside the filtered assets array, it should be disabled as it's file type isn't accepted.
			let isDisabled = !acceptedFileFormatAssets.find(accepted => accepted.id === asset.id);

			return {
				...asset,
				isSelected, // Used for row highlighting
				isDisabled, // Used for row highlighting
				idSelector: {
					id: asset.id,
					builtId: buildAssetId(asset),
					isSelected, // Used for checked status
					isDisabled, // Used for disabled status
					selector,
				},
			};
		});
	}, [selectedIds, assets.items, selector]);

	let isAllSelected = useMemo(() => {
		if(!mappedAssets.length){ return false; }
		return mappedAssets.every(asset => asset.isDisabled || selectedIds.includes(asset.id));
	}, [selectedIds, mappedAssets]);

	function onNameClick(data) {
		selector([data.id], !data.isSelected);
	}

	function openPreviewModal(dependentValues) {
		function ConfiguredPreviewModal(){
			let modalProps = {
				libraryId,
				...dependentValues,
			};
			return (
				<PreviewAssetsModal
					{...modalProps}
					onClose = {popModal}
				/>
			);
		}

		pushModal(ConfiguredPreviewModal);
	}

	const selectorColumn = (!multiple) ? SingleSelectColumn : {
		...MultiSelectColumn,
		headerRenderer: <SelectAllCell onChange={selectAll} />,
	};

	const mappedColumns = COLUMNS.map((col) => {
		if (col.name === "File Name") {
			col.onClick = onNameClick;
		}

		if(col.name === "Actions") {
			col.formatter = (props) => {
				const { dependentValues } = props;
				const { assetType } = dependentValues;

				if (isTextAsset(assetType) || isAfterEffectsAsset(assetType)) {
					return null;
				}

				return (
					<Icon
						className="preview-asset-action bounce-circle"
						data-tip="Preview"
						name="far fa-eye"
						onClick={() => openPreviewModal(dependentValues)}
					/>
				);
			};
		}

		return col;
	});

	const columns = [selectorColumn, ...mappedColumns];

	const { libraryId } = useParams();

	const {
		filterInfo: { page, rpp, searchTerm, sort },
	} = useCollectionFiltering(
		assets,
		Assets.setAdvancedFilter,
		() => {
			dispatch(Assets.clearAssets())
				.then(() => {
					dispatch(Assets.loadAssets(libraryId));
				});
		},
		() => {
			// If a configureFilter prop is provided, use it here, instead of the default configuration
			if(configureFilter){
				configureFilter();
				return;
			}
			/*
				last modified descending is the default, but just in case.  We currently get the newly uploaded asset
				record on upload success to inject it into the grid directly, so this is the surest way to have
				new uploads show up on the grid and not confuse users.
			*/
			dispatch(Assets.setSort("lastModified", "DESC"));
			dispatch(Assets.updateColumnFilter("assetType", { params: [capFirst(type)]}));

			if(/^video$/i.test(type)){
				if(allowEncodedVideo === false){
					dispatch(Assets.updateColumnFilter("encoded", { params: ["false"]}));
				} else if(allowUnencodedVideo === false){
					dispatch(Assets.updateColumnFilter("encoded", { params: ["true"]}));
				}
			}
		},
		() => {
			dispatch(Assets.clearColumnFilters());
		}
	);

	/*
		We use `hasAssets` to determine what content to show.  If it's `false`, we show the no assets screen.

		If we're still loading, `hasAssets` is undefined (i.e. we don't know), so we show the spinner.
		If we're finished loading, and have assets, or we've filtered with a search term, it's true.

		This is somewhat fragile if we were to introduce column filtering in this widget, but if we did,
		we can use `activeFilters()` to check if we're only filtered on asset type, which we have to do to get
		records in the combined Asset endpoint format that this grid relies on.

		But as it stands, we only have to differentiate between no records of a type, and search returning nothing.
	*/
	const hasAssets = assets.isLoaded ? (assets.items.length || searchTerm) : undefined;

	const pagerProps = {
		showItemCount: true,
		initialPage: page,
		forcePage: page,
		pageSize: rpp,
		pageCount: assets.totalPages,
		maxItems: assets.totalItems,
		showItemWord: "",
		rowCountItems: CREATIVE_ROW_COUNT_OPTIONS,

		disabled: !assets.isLoaded,

		onPageChange: newPage => dispatch(Assets.setPage(newPage)),
		onRowCountChange: newRPP => dispatch(Assets.setRPP(newRPP)),
	};

	function Content(){
		let displayedContent;

		if(hasAssets !== undefined){
			/* While hasAssets is undefined, we'll just see a spinner with nothing behind it. */
			if(!hasAssets){
				const buttons = [];
				if(canUpload){
					buttons.push(<Button key="upload-asset" onClick={selectFiles}>{`Upload ${typeLabel}s`}</Button>);
				}
				displayedContent = (
					<AssetSocketListener>
						<EmptySection
							className="empty-assets"
							header="No assets have been added"
							src="/images/noAssets.svg"
							buttons = {buttons}
						/>
					</AssetSocketListener>
				);
			} else {
				displayedContent = (
					<>
						<ProgressIndicator isLoading={isSubmitting} />
						<Toolbar
							search = {true}
							searchValue = {searchTerm}
							searchDebounce = {SEARCH_DEBOUNCE}
							onSearch = {newSearchTerm => dispatch(Assets.setSearchTerm(newSearchTerm))}
						>
							{canUpload && (
								<a className="upload-link" onClick={selectFiles}>
									<Icon name="far fa-cloud-upload" />
									{`Upload ${typeLabel}s`}
								</a>
							)}
						</Toolbar>
						<div className="grid">
							<AssetGrid
								assets = {mappedAssets}
								isLoading={!assets.isLoaded}
								sort={sort} // Needed to display the initial column sort order indicator
								columns = {columns}
								resetScroll = {false}
							/>
						</div>
						<Pager {...pagerProps} />
					</>
				);
			}
		}

		return (
			<div >
				{Breadcrumb}
				<div className="select-asset-modal-content">
					<ProgressIndicator isLoading={hasAssets === undefined} />
					{displayedContent}
				</div>
			</div>
		);
	}

	function Footer(){
		function attemptSubmit(){
			/*
				If `onSelect` returns a promise, we don't pop the modal.

				If a parent modal wants to pop, it can do that itself, because it doesn't really matter
				who calls popModal.

				This allows us to do things like submit forms directly from this modal, and then close
				the entire modal stack from the `onSelect` handler.
			*/
			setIsSubmitting(true);

			let ret = onSelect(selectedIds, assets?.items);
			if(!(ret?.then)){
				popModal();
			}
		}

		let buttonText = submitButtonText || `Select ${typeLabel}`;
		if(multiple && selectedIds.length){
			buttonText = `${buttonText} (${selectedIds.length})`;
		}

		let selectButton = hasAssets && (
			<Button
				theme={submitButtonTheme}
				disabled = {!hasSelection || isSubmitting}
				className = {isSubmitting ? "circle-load" : ""}
				icon = {isSubmitting ? "spinner" : null}
				onClick={attemptSubmit}
			>
				{buttonText}
			</Button>
		);

		return (
			<Layout.ModalFooter>
				<Button
					theme="default"
					onClick={popModal}
					disabled={isSubmitting}
				>
					Cancel
				</Button>
				{selectButton}
			</Layout.ModalFooter>
		);
	}

	let modalProps = {
		...commonModalProps,
		xClose: !isSubmitting,
		className: `${commonModalProps.className} select-asset-modal`,
		renderContent: Content,
		renderFooter: Footer,
		onClose: popModal,
	};

	return (
		<SelectionContext.Provider value={{ isAllSelected, selectAll }}>
			<Modal
				{...modalProps}
			/>
		</SelectionContext.Provider>
	);
}

SelectAssets.displayName = "SelectAssets";
SelectAssets.defaultProps = {
	type:"image",
	onSelect:noop,
	accept: "*",
};
SelectAssets.propTypes = {
	type:PropTypes.oneOf(["image", "video", "richloads", "audio", "text", "afterEffects"]),
	onSelect:PropTypes.func,
};
